
import React from 'react';
import MainLayout from "@/components/layout/MainLayout";
import { useLanguage } from '@/context/LanguageContext';
import { Mail, MapPin, Star, Users, MessageCircle, Target, Heart, Lightbulb } from 'lucide-react';

const SectionHeader = ({ 
  icon: Icon, 
  title, 
  subtitle 
}: { 
  icon: React.ElementType; 
  title: string; 
  subtitle?: string; 
}) => (
  <div className="flex items-start space-x-4 mb-8">
    <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mt-1">
      <Icon className="h-4 w-4 text-orange-600" />
    </div>
    <div>
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{title}</h2>
      {subtitle && <p className="text-gray-600 text-lg">{subtitle}</p>}
    </div>
  </div>
);

const About = () => {
  const { language } = useLanguage();
  const isChineseMode = language === 'zh-TW';

  return (
    <MainLayout>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="pt-24 pb-16 px-4 md:px-8 lg:px-12 bg-gradient-to-b from-orange-50 to-white">
          <div className="container max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {isChineseMode ? "關於 TruStay" : "About TruStay"}
            </h1>
            <div className="w-24 h-1 bg-orange-500 mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              {isChineseMode 
                ? "由租客發起的租屋經驗分享平台，致力於打造透明、互助、公平的租屋生態"
                : "A tenant-initiated rental experience sharing platform, committed to creating a transparent, mutual, and fair rental ecosystem"
              }
            </p>
          </div>
        </section>

        {/* Who We Are Section */}
        <section className="py-16 px-4 md:px-8 lg:px-12">
          <div className="container max-w-4xl mx-auto">
            <SectionHeader 
              icon={Users}
              title={isChineseMode ? "我們是誰" : "Who We Are"}
            />
            <div className="bg-gray-50 rounded-xl p-8 border-l-4 border-orange-500">
              <p className="text-gray-700 text-lg leading-relaxed">
                {isChineseMode 
                  ? "TruStay 是由租客發起的租屋經驗分享平台，致力於打造一個透明、互助、公平的租屋生態。我們相信，透過經驗的力量，可以幫助全球的租客避開風險、找到值得信賴的房源。"
                  : "TruStay is a tenant-initiated rental experience sharing platform, dedicated to creating a transparent, mutual, and fair rental ecosystem. We believe that through the power of experience, we can help tenants worldwide avoid risks and find trustworthy properties."
                }
              </p>
            </div>
          </div>
        </section>

        {/* Why We Started Section */}
        <section className="py-16 px-4 md:px-8 lg:px-12 bg-orange-50">
          <div className="container max-w-4xl mx-auto">
            <SectionHeader 
              icon={Lightbulb}
              title={isChineseMode ? "為什麼創立 TruStay" : "Why We Started TruStay"}
            />
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8 shadow-sm border-l-4 border-orange-500">
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  {isChineseMode 
                    ? "創辦人曾在加拿大遇到一位看似友善的房東，搬出後卻被以清潔不當為由，無預警扣除 500 加幣的押金。這段經歷讓我們意識到：租屋市場資訊不透明，租客沒有保障，改變勢在必行。"
                    : "Our founder once encountered a seemingly friendly landlord in Canada who, after moving out, unexpectedly deducted CAD$500 from the security deposit citing inadequate cleaning. This experience made us realize: the rental market lacks transparency, tenants have no protection, and change is imperative."
                  }
                </p>
                <div className="border-t border-orange-200 pt-6">
                  <p className="text-gray-700 text-lg leading-relaxed font-medium">
                    {isChineseMode 
                      ? "因此，我們創立了 TruStay，一個讓租客彼此分享、揭露與肯定房東的平台，從租房經驗出發，改善整體市場。"
                      : "Therefore, we created TruStay, a platform where tenants can share experiences, expose issues, and recognize good landlords, starting from rental experiences to improve the overall market."
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Mission Section */}
        <section className="py-16 px-4 md:px-8 lg:px-12">
          <div className="container max-w-4xl mx-auto">
            <SectionHeader 
              icon={Target}
              title={isChineseMode ? "我們的使命" : "Our Mission"}
            />
            <div className="space-y-4">
              <div className="flex items-start space-x-4 p-6 bg-orange-50 rounded-lg border-l-4 border-orange-500">
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-3"></div>
                <p className="text-gray-700 text-lg">
                  {isChineseMode 
                    ? "打破資訊不對稱，讓租客的真實評價成為指南"
                    : "Break information asymmetry and make tenants' real reviews a guide"
                  }
                </p>
              </div>
              <div className="flex items-start space-x-4 p-6 bg-orange-50 rounded-lg border-l-4 border-orange-500">
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-3"></div>
                <p className="text-gray-700 text-lg">
                  {isChineseMode 
                    ? "鼓勵好房東被看見，減少壞房東為所欲為"
                    : "Encourage good landlords to be recognized, reduce bad landlords' misconduct"
                  }
                </p>
              </div>
              <div className="flex items-start space-x-4 p-6 bg-orange-50 rounded-lg border-l-4 border-orange-500">
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-3"></div>
                <p className="text-gray-700 text-lg">
                  {isChineseMode 
                    ? "建立信譽與媒合系統，讓租屋更像 LinkedIn x Airbnb"
                    : "Build a reputation and matching system, making rental more like LinkedIn x Airbnb"
                  }
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Features Section */}
        <section className="py-16 px-4 md:px-8 lg:px-12 bg-orange-50">
          <div className="container max-w-4xl mx-auto">
            <SectionHeader 
              icon={Star}
              title={isChineseMode ? "我們提供什麼功能" : "What Features We Offer"}
            />
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {isChineseMode ? "租屋地圖" : "Rental Map"}
                </h3>
                <p className="text-gray-600">
                  {isChineseMode ? "結合地點與真實評價，避開地雷" : "Combine locations with real reviews to avoid problematic properties"}
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {isChineseMode ? "我要分享" : "Share Experience"}
                </h3>
                <p className="text-gray-600">
                  {isChineseMode ? "投稿租屋經驗，影響未來的租客" : "Submit rental experiences to help future tenants"}
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {isChineseMode ? "租房攻略" : "Rental Guides"}
                </h3>
                <p className="text-gray-600">
                  {isChineseMode ? "整理租前、租中、退租所有重點" : "Comprehensive guides for pre-rental, during tenancy, and move-out"}
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {isChineseMode ? "會員制度與積分任務" : "Membership & Points System"}
                </h3>
                <p className="text-gray-600">
                  {isChineseMode ? "完成任務獲得積分與回饋" : "Complete tasks to earn points and rewards"}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Vision Section */}
        <section className="py-16 px-4 md:px-8 lg:px-12">
          <div className="container max-w-4xl mx-auto">
            <SectionHeader 
              icon={Heart}
              title={isChineseMode ? "我們的願景" : "Our Vision"}
            />
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-8 md:p-12 text-white">
              <h3 className="text-2xl font-bold mb-6">
                {isChineseMode ? "讓「租房不再靠運氣」" : "Making Rental 'No Longer Based on Luck'"}
              </h3>
              <p className="text-lg leading-relaxed mb-6 opacity-95">
                {isChineseMode 
                  ? "我們從加拿大與台灣出發，期望擴展到全球，打造一個由租客主導的租屋生態系。讓房東學會自我經營形象，讓租客取得話語權。"
                  : "Starting from Canada and Taiwan, we aim to expand globally and create a tenant-led rental ecosystem. We want landlords to learn how to manage their reputation while giving tenants a voice."
                }
              </p>
              <div className="border-t border-orange-400 pt-6">
                <p className="text-xl font-semibold">
                  {isChineseMode 
                    ? "我們不只是平台，更是一場租屋文化的革命。"
                    : "We are not just a platform, but a revolution in rental culture."
                  }
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 px-4 md:px-8 lg:px-12 bg-gray-50">
          <div className="container max-w-4xl mx-auto">
            <SectionHeader 
              icon={Mail}
              title={isChineseMode ? "聯絡我們" : "Contact Us"}
            />
            <div className="bg-white rounded-xl p-8 shadow-sm border-l-4 border-orange-500">
              <p className="text-lg text-gray-700 mb-6 text-center">
                {isChineseMode 
                  ? "若您對 TruStay 有任何建議或想分享經驗，歡迎隨時來信："
                  : "If you have any suggestions for TruStay or would like to share your experience, please feel free to contact us:"
                }
              </p>
              <div className="text-center">
                <div className="inline-flex items-center justify-center space-x-3 bg-orange-50 px-6 py-4 rounded-lg border border-orange-200">
                  <Mail className="h-5 w-5 text-orange-600 flex-shrink-0" />
                  <span className="font-semibold text-orange-700 break-all">customer.service@trustay.app</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </MainLayout>
  );
};

export default About;
